import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { useWebSocket } from '@/hooks/use-ws'
import { useLang } from '@/hooks/use-lang'
import { useIsMobile } from '@/hooks/use-media-query'
import { t } from '@/lib/i18n'
import { formatTime, formatShortDate } from '@/lib/time'
import { ProviderMark } from '@/components/provider-mark'
import { Select } from '@/components/select'
import { EventInspector } from '@/components/event-inspector'
import { ResizeHandle } from '@/components/resize-handle'
import { clampPaneWidth, readPaneWidth, writePaneWidth, defaultPaneWidth, PANE_MIN_PX } from '@/lib/pane-width'
import { useAuth } from '@/hooks/use-auth'
import { useCopy } from '@/hooks/use-copy'
import type { WebhookEvent, Endpoint, Tenant, TimelinePoint } from '@/lib/types'

interface PaginatedEvents {
  events: WebhookEvent[]
  total: number
  page: number
  limit: number
}

const PAGE_LIMIT = 50

// Filters AND the selected event live in the URL so the back button, refresh,
// and shared links all restore the same view. `event` is the inline-inspected
// row (Discover pane on desktop); the rest are the faceted filters.
interface Filters {
  status: string
  search: string
  provider: string
  endpointId: string
  range: string
  page: number
}

// rangeToFrom converts a UI time-range token into an RFC3339 lower bound the
// events API understands (?from=). '' (all time) → null (no bound).
const RANGE_MS: Record<string, number> = {
  '1h': 3600e3,
  '24h': 86_400e3,
  '7d': 7 * 86_400e3,
  '30d': 30 * 86_400e3,
  '90d': 90 * 86_400e3,
}
function rangeToFrom(range: string): string | null {
  const ms = RANGE_MS[range]
  return ms ? new Date(Date.now() - ms).toISOString() : null
}

// The heartbeat strip mirrors the selected range so its count, success rate and
// chart stay consistent with the filtered list. The analytics timeline API
// speaks 1h/24h/7d/30d/90d; all-time ('') falls back to the 24h pulse.
function rangeToPeriod(range: string): string {
  return RANGE_MS[range] ? range : '24h'
}

// Heartbeat title per period (the "Activity · last …" line above the chart).
const HEARTBEAT_LABEL: Record<string, string> = {
  '1h': 'heartbeatLast1h',
  '24h': 'heartbeatLast24h',
  '7d': 'heartbeatLast7d',
  '30d': 'heartbeatLast30d',
  '90d': 'heartbeatLast90d',
}

// docsUrl points at the vitrine docs on the SAME environment as the app:
// app.staging.webhooklens.cloud → staging.webhooklens.cloud/docs.html,
// app.webhooklens.cloud → webhooklens.cloud/docs.html. Falls back to prod
// docs for local dev / unknown hosts.
function docsUrl(): string {
  if (typeof window !== 'undefined') {
    const h = window.location.hostname
    if (h.endsWith('webhooklens.cloud')) {
      return `https://${h.replace(/^app\./, '')}/docs.html`
    }
  }
  return 'https://webhooklens.cloud/docs.html'
}

function readFilters(params: URLSearchParams): Filters {
  return {
    status: params.get('status') ?? '',
    search: params.get('q') ?? '',
    provider: params.get('provider') ?? '',
    endpointId: params.get('endpoint') ?? '',
    range: params.get('range') ?? '',
    page: Math.max(1, parseInt(params.get('page') ?? '1', 10) || 1),
  }
}

export function EventsPage() {
  useLang()
  const { user } = useAuth()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const [paneWidth, setPaneWidth] = useState(() =>
    typeof window !== 'undefined' ? readPaneWidth(window.innerWidth) : 400
  )
  const paneMax = typeof window !== 'undefined' ? Math.round(window.innerWidth * 0.6) : 800
  const setClampedPaneWidth = (px: number) => {
    const w = clampPaneWidth(px, window.innerWidth)
    setPaneWidth(w)
    writePaneWidth(w)
  }
  const resetPaneWidth = () => {
    const w = defaultPaneWidth(window.innerWidth)
    setPaneWidth(w)
    writePaneWidth(w)
  }
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const filters = useMemo(() => readFilters(searchParams), [searchParams])

  // Time-range options for the segmented selector. Tokens (24h, 7d…) match the
  // RANGE_MS keys; '' is all-time. Built per render so the "All" label follows
  // the active language.
  const RANGE_OPTIONS: { value: string; label: string }[] = [
    { value: '', label: t('rangeAllShort') },
    { value: '1h', label: '1h' },
    { value: '24h', label: '24h' },
    { value: '7d', label: '7d' },
    { value: '30d', label: '30d' },
    { value: '90d', label: '90d' },
  ]

  // Selected event = the Discover pane on desktop. On mobile there is no room
  // for a split, so a tap navigates to the full-screen /events/:id instead.
  const selectedId = searchParams.get('event') ?? ''
  const paneOpen = !!selectedId && !isMobile
  const searchRef = useRef<HTMLInputElement>(null)

  // Local debounced search input — only the committed value reaches the URL so
  // typing doesn't blow out browser history with intermediate entries.
  const [searchInput, setSearchInput] = useState(filters.search)
  useEffect(() => { setSearchInput(filters.search) }, [filters.search])
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) updateFilters({ search: searchInput, page: 1 })
    }, 300)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput])

  // Patch the filter keys while preserving the `event` selection (and anything
  // else) already in the URL.
  function updateFilters(patch: Partial<Filters>) {
    const next = { ...filters, ...patch }
    const sp = new URLSearchParams(searchParams)
    next.status ? sp.set('status', next.status) : sp.delete('status')
    next.search ? sp.set('q', next.search) : sp.delete('q')
    next.provider ? sp.set('provider', next.provider) : sp.delete('provider')
    next.endpointId ? sp.set('endpoint', next.endpointId) : sp.delete('endpoint')
    next.range ? sp.set('range', next.range) : sp.delete('range')
    next.page > 1 ? sp.set('page', String(next.page)) : sp.delete('page')
    setSearchParams(sp, { replace: true })
  }

  function selectEvent(id: string) {
    const sp = new URLSearchParams(searchParams)
    sp.set('event', id)
    setSearchParams(sp) // push, so the back button closes the pane
  }
  function closePane() {
    const sp = new URLSearchParams(searchParams)
    sp.delete('event')
    setSearchParams(sp, { replace: true })
  }
  // Open a row: pane on desktop, full-screen route on mobile.
  function openEvent(id: string) {
    if (isMobile) navigate(`/events/${id}`)
    else selectEvent(id)
  }

  // A shared (desktop) link opened on a phone carries a stale ?event=; clear it
  // so a later resize up to desktop doesn't pop open a pane nobody selected.
  useEffect(() => {
    if (isMobile && selectedId) closePane()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile])

  const { connected, lastEvent } = useWebSocket()

  const { data: endpoints = [] } = useQuery<Endpoint[]>({
    queryKey: ['endpoints'],
    queryFn: () => api.get<Endpoint[]>('/api/endpoints'),
  })

  const { data: tenant } = useQuery<Tenant>({
    queryKey: ['tenant'],
    queryFn: () => api.get<Tenant>('/api/tenant'),
    enabled: !!user,
  })

  const params = new URLSearchParams()
  params.set('limit', String(PAGE_LIMIT))
  params.set('page', String(filters.page))
  if (filters.status) params.set('status', filters.status)
  if (filters.search) params.set('search', filters.search)
  if (filters.provider) params.set('provider', filters.provider)
  if (filters.endpointId) params.set('endpoint_id', filters.endpointId)
  const fromBound = rangeToFrom(filters.range)
  if (fromBound) params.set('from', fromBound)

  const { data, isLoading, isError, refetch } = useQuery<PaginatedEvents>({
    queryKey: ['events', filters.status, filters.search, filters.provider, filters.endpointId, filters.range, filters.page],
    queryFn: () => api.get<PaginatedEvents>(`/api/events?${params}`),
  })

  // Heartbeat: timeline for the selected range (all-time falls back to 24h),
  // re-polled every minute so the bars animate as new events land. Sharing the
  // range keeps the strip's count + success rate consistent with the list.
  const tlPeriod = rangeToPeriod(filters.range)
  const { data: timeline = [] } = useQuery<TimelinePoint[]>({
    queryKey: ['analytics-timeline', tlPeriod],
    queryFn: () => api.get<TimelinePoint[]>(`/api/analytics/timeline?period=${tlPeriod}`),
    staleTime: 60_000,
    refetchInterval: 60_000,
  })
  const [hoverBucket, setHoverBucket] = useState<number | null>(null)

  // Fresh-event tracking: a new WS event is marked fresh for 1.2s so its row
  // gets the slide-in. Without this the list just snaps in, which feels dead.
  const [freshIds, setFreshIds] = useState<Set<string>>(new Set())

  // Live refresh, coalesced. A burst of webhooks must NOT invalidate per event:
  // that fires one /api/events + /api/analytics/timeline refetch per event, which
  // under load trips the per-tenant API rate limit (and can log the user out).
  // Instead we refetch at most once per REFRESH_MS — leading edge for instant
  // feel when traffic is light, trailing edge to batch a burst.
  const REFRESH_MS = 1500
  const lastRefresh = useRef(0)
  const trailing = useRef<ReturnType<typeof setTimeout> | null>(null)
  const freshPending = useRef<string[]>([])

  useEffect(() => () => { if (trailing.current) clearTimeout(trailing.current) }, [])

  useEffect(() => {
    if (!lastEvent) return
    if (filters.page !== 1) return
    freshPending.current.push(lastEvent.id)

    const flush = () => {
      lastRefresh.current = Date.now()
      trailing.current = null
      queryClient.invalidateQueries({ queryKey: ['events'] })
      queryClient.invalidateQueries({ queryKey: ['analytics-timeline'] })
      const ids = freshPending.current
      freshPending.current = []
      if (ids.length === 0) return
      setFreshIds(prev => { const next = new Set(prev); ids.forEach(i => next.add(i)); return next })
      setTimeout(() => setFreshIds(prev => {
        const next = new Set(prev); ids.forEach(i => next.delete(i)); return next
      }), 1200)
    }

    const since = Date.now() - lastRefresh.current
    if (since >= REFRESH_MS) {
      flush()
    } else if (!trailing.current) {
      trailing.current = setTimeout(flush, REFRESH_MS - since)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastEvent?.id])

  const events = data?.events ?? []
  const total = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / PAGE_LIMIT))
  const hasFilters = !!filters.status || !!filters.search || !!filters.provider || !!filters.endpointId || !!filters.range

  const endpointNameById = useMemo(() => {
    const m: Record<string, string> = {}
    for (const ep of endpoints) m[ep.id] = ep.name
    return m
  }, [endpoints])

  const proxyBaseUrl = tenant?.proxy_base_url ?? `/wh/<your-tenant>`
  const exampleUrl = `${proxyBaseUrl}/${endpoints[0]?.slug ?? '<your-endpoint>'}`

  function clearAll() { updateFilters({ status: '', search: '', provider: '', endpointId: '', range: '', page: 1 }) }

  // Provider chips — derived from actually-seen providers (up to 4), toggling
  // the real `provider` filter (exact match) rather than a free-text search.
  const providerChips = useMemo(() => {
    const seen = new Set<string>()
    // Keep the active provider facet visible even if it's absent from the
    // current page's events, so an applied filter never silently vanishes.
    if (filters.provider) seen.add(filters.provider)
    for (const e of events) {
      if (e.provider && !seen.has(e.provider)) seen.add(e.provider)
      if (seen.size >= 4) break
    }
    return Array.from(seen)
  }, [events, filters.provider])

  const errorCount = useMemo(() => events.filter(e => e.status === 'failed').length, [events])
  const avgLatency = useMemo(() => {
    const withLat = events.filter(e => e.response_time_ms != null)
    if (withLat.length === 0) return null
    const sum = withLat.reduce((acc, e) => acc + (e.response_time_ms || 0), 0)
    return Math.round(sum / withLat.length)
  }, [events])

  // Keyboard triage (desktop): Cmd/Ctrl+K or / focuses search, arrows / j k move
  // the selection (the pane follows via ?event=), Escape closes the pane. The
  // selection is already URL-driven, so moving it is just a param swap.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const el = e.target as HTMLElement | null
      const typing = !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable)
      if (e.key === 'Escape') {
        if (selectedId && !isMobile) {
          setSearchParams(prev => { const sp = new URLSearchParams(prev); sp.delete('event'); return sp }, { replace: true })
        }
        return
      }
      if (typing) return
      if (e.key === '/' || ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k')) {
        e.preventDefault()
        searchRef.current?.focus()
        return
      }
      if (isMobile || events.length === 0) return
      const down = e.key === 'ArrowDown' || e.key === 'j'
      const up = e.key === 'ArrowUp' || e.key === 'k'
      if (!down && !up) return
      e.preventDefault()
      const idx = events.findIndex(ev => ev.id === selectedId)
      const next = idx === -1
        ? (down ? 0 : events.length - 1)
        : Math.min(events.length - 1, Math.max(0, idx + (down ? 1 : -1)))
      const id = events[next].id
      setSearchParams(prev => { const sp = new URLSearchParams(prev); sp.set('event', id); return sp })
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [events, selectedId, isMobile, setSearchParams])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Top bar — title + LIVE + status pill + Docs + search. Wraps on narrow. */}
      <div style={{
        padding: '0.875rem 1.25rem 0.75rem',
        borderBottom: '1px solid var(--border)',
        backgroundColor: 'var(--surface)',
        display: 'flex', flexDirection: 'column', gap: '0.625rem',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <h2 style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--white)', letterSpacing: '-0.005em' }}>
            {t('stream')}
          </h2>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
            fontFamily: 'var(--font-mono, ui-monospace, monospace)',
            fontSize: '0.6875rem', letterSpacing: '0.04em',
            color: connected ? 'var(--green)' : 'var(--muted)',
          }}>
            <span
              aria-hidden="true"
              style={{
                width: '0.4375rem', height: '0.4375rem', borderRadius: '9999px',
                backgroundColor: connected ? 'var(--green)' : 'var(--muted)',
                animation: connected ? 'ping-soft 2s ease-in-out infinite' : undefined,
              }}
            />
            {connected ? 'LIVE' : 'OFF'}
          </span>
          <span style={{ flex: 1, minWidth: '0.5rem' }} />
          <span style={{
            fontSize: '0.6875rem', fontWeight: 500,
            padding: '3px 8px', borderRadius: 999,
            backgroundColor: total === 0
              ? 'color-mix(in srgb, var(--accent) 14%, transparent)'
              : 'var(--surface-2)',
            color: total === 0 ? 'var(--accent-ink)' : 'var(--muted)',
            letterSpacing: '0.005em', whiteSpace: 'nowrap',
          }}>
            {total === 0
              ? t('noEventsYet')
              : t('eventsCount', { count: total })}
          </span>
          <a
            href={docsUrl()}
            target="_blank"
            rel="noreferrer"
            style={{
              fontSize: '0.6875rem', fontWeight: 500,
              padding: '3px 9px', borderRadius: 5,
              border: '1px solid var(--border)',
              color: 'var(--text)', backgroundColor: 'var(--bg)',
              textDecoration: 'none', whiteSpace: 'nowrap',
            }}
          >
            {t('docs')}
          </a>
          <SearchBar value={searchInput} onChange={setSearchInput} inputRef={searchRef} />
        </div>

        <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <Chip active={!filters.status} onClick={() => updateFilters({ status: '', page: 1 })}>
            {t('all')}
          </Chip>
          <Chip
            active={filters.status === 'failed'}
            onClick={() => updateFilters({ status: filters.status === 'failed' ? '' : 'failed', page: 1 })}
          >
            {t('errorsOnly')}
          </Chip>
          {providerChips.map(p => (
            <Chip
              key={p}
              active={filters.provider === p}
              onClick={() => updateFilters({ provider: filters.provider === p ? '' : p, page: 1 })}
            >
              {p}
            </Chip>
          ))}
          {/* Time-range selector — segmented mono pill group, matching Analytics. */}
          <div
            role="group"
            aria-label={t('filterByTime')}
            style={{
              display: 'flex',
              marginLeft: 'auto',
              border: '1px solid var(--border-light)',
              borderRadius: 6,
              overflow: 'hidden',
            }}
          >
            {RANGE_OPTIONS.map((opt, i) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => updateFilters({ range: opt.value, page: 1 })}
                className="tap"
                aria-pressed={filters.range === opt.value}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '5px 12px',
                  border: 'none',
                  background: filters.range === opt.value ? 'var(--white)' : 'var(--bg)',
                  color: filters.range === opt.value ? 'var(--bg)' : 'var(--muted)',
                  fontFamily: 'var(--font-mono, ui-monospace, monospace)',
                  fontSize: 11.5,
                  cursor: 'pointer',
                  borderRight: i < RANGE_OPTIONS.length - 1 ? '1px solid var(--border)' : 'none',
                  transition: 'all 140ms cubic-bezier(0.22, 1, 0.36, 1)',
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {endpoints.length > 1 && (
            <Select
              size="sm"
              pill
              aria-label={t('filterByEndpoint')}
              value={filters.endpointId}
              onChange={e => updateFilters({ endpointId: e.target.value, page: 1 })}
              style={{ maxWidth: '45vw' }}
            >
              <option value="">{t('allEndpoints')}</option>
              {endpoints.map(ep => (
                <option key={ep.id} value={ep.id}>{ep.name}</option>
              ))}
            </Select>
          )}
        </div>
      </div>

      {/* Heartbeat — 24h scrubber. Hidden until events exist. */}
      {timeline.length > 0 && (
        <HeartbeatStrip data={timeline} period={tlPeriod} active={hoverBucket} onHover={setHoverBucket} />
      )}

      {/* Body — list, plus the Discover detail pane alongside it on desktop. */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
        <div style={{ flex: 1, minWidth: 0, overflow: 'auto', backgroundColor: 'var(--bg)' }}>
          {isLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '8rem', color: 'var(--muted)', fontSize: '0.875rem' }}>
              {t('loading')}
            </div>
          ) : isError ? (
            <EmptyState
              icon={
                <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} style={{ color: 'var(--red)' }}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m0 3.75h.008M10.34 3.94l-7.5 13A1.5 1.5 0 004.16 19.5h15.68a1.5 1.5 0 001.32-2.56l-7.5-13a1.5 1.5 0 00-2.62 0z" />
                </svg>
              }
              title={t('couldntLoadEvents')}
              description={t('checkConnection')}
              action={<button type="button" onClick={() => refetch()} style={{ color: 'var(--accent)', fontSize: '0.75rem', fontWeight: 500, background: 'transparent', border: 'none', cursor: 'pointer' }}>{t('retry')}</button>}
            />
          ) : events.length === 0 ? (
            hasFilters ? (
              <EmptyState
                icon={
                  <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} style={{ color: 'var(--muted)' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                }
                title={t('noEventsFound')}
                description={t('tryAdjustingFilters')}
                action={<button type="button" onClick={clearAll} style={{ color: 'var(--accent)', fontSize: '0.75rem', fontWeight: 500, background: 'transparent', border: 'none', cursor: 'pointer' }}>{t('clearFilters')}</button>}
              />
            ) : (
              <FirstRunEmptyState exampleUrl={exampleUrl} />
            )
          ) : (
            <>
              <StreamTableHeader compact={paneOpen} />
              {events.map(event => (
                <StreamRow
                  key={event.id}
                  event={event}
                  endpointName={endpointNameById[event.endpoint_id]}
                  fresh={freshIds.has(event.id)}
                  selected={selectedId === event.id}
                  compact={paneOpen}
                  isMobile={isMobile}
                  onActivate={() => openEvent(event.id)}
                />
              ))}
            </>
          )}
        </div>

        {paneOpen && (
          <>
            <ResizeHandle
              width={paneWidth}
              min={PANE_MIN_PX}
              max={paneMax}
              onResize={setClampedPaneWidth}
              onReset={resetPaneWidth}
            />
            <DetailPane eventId={selectedId} onClose={closePane} width={paneWidth} />
          </>
        )}
      </div>

      {/* Foot status — streaming state, counts, page nav. */}
      {total > 0 && (
        <FootStatus
          streaming={connected && !hasFilters}
          loaded={events.length}
          errors={errorCount}
          avgMs={avgLatency}
          page={filters.page}
          totalPages={totalPages}
          onPrev={() => updateFilters({ page: Math.max(1, filters.page - 1) })}
          onNext={() => updateFilters({ page: Math.min(totalPages, filters.page + 1) })}
        />
      )}
    </div>
  )
}

// SearchBar — mono-styled, flexible width so it shares the top row on desktop
// and wraps to its own line on narrow viewports.
function SearchBar({ value, onChange, inputRef }: { value: string; onChange: (v: string) => void; inputRef?: React.RefObject<HTMLInputElement | null> }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '4px 10px',
      border: '1px solid var(--border-light)',
      borderRadius: 6,
      flex: '1 1 200px', maxWidth: 260, minWidth: 0,
      backgroundColor: 'var(--bg)',
    }}>
      <span style={{ color: 'var(--muted)', fontSize: 13, lineHeight: 1 }}>⌕</span>
      <input
        ref={inputRef}
        type="text"
        placeholder={t('searchPlaceholder')}
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{
          border: 'none', outline: 'none', background: 'transparent',
          fontFamily: 'var(--font-mono, ui-monospace, monospace)',
          fontSize: 12,
          color: 'var(--text)',
          width: '100%', minWidth: 0,
        }}
      />
      <span style={{
        display: 'inline-flex', padding: '1px 5px', fontSize: 10.5,
        border: '1px solid var(--border-light)', borderBottomWidth: 2,
        borderRadius: 4, backgroundColor: 'var(--surface)', color: 'var(--muted)',
        lineHeight: '14px', flexShrink: 0,
        fontFamily: 'var(--font-mono, ui-monospace, monospace)',
        whiteSpace: 'nowrap',
      }}>⌘K</span>
    </div>
  )
}

// Chip — pill filter, active state inverts.
function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '4px 10px',
        border: `1px solid ${active ? 'var(--white)' : 'var(--border-light)'}`,
        borderRadius: 999,
        background: active ? 'var(--white)' : 'var(--bg)',
        color: active ? 'var(--bg)' : 'var(--text)',
        fontSize: 11.5,
        fontWeight: 500,
        cursor: 'pointer',
        transition: 'all 140ms cubic-bezier(0.22, 1, 0.36, 1)',
      }}
    >
      {children}
    </button>
  )
}

// StreamTableHeader — column labels above the rows. Drops the time/endpoint/
// latency columns in the compact (pane-open) layout, matching StreamRow.
function StreamTableHeader({ compact }: { compact: boolean }) {
  const cellStyle: React.CSSProperties = {
    fontSize: 10.5,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: 'var(--muted)',
    fontWeight: 500,
  }
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: compact ? '24px 18px 1fr 64px' : '24px 18px 130px 1fr 110px 72px 68px',
      gap: 0,
      padding: '0 16px',
      height: 30,
      alignItems: 'center',
      borderBottom: '1px solid var(--border)',
      backgroundColor: 'var(--surface)',
      position: 'sticky', top: 0, zIndex: 1,
    }}>
      <span></span>
      <span></span>
      {!compact && <span style={cellStyle}>{t('time')}</span>}
      <span style={cellStyle}>{t('event')}</span>
      {!compact && <span style={cellStyle}>{t('endpoint')}</span>}
      <span style={cellStyle}>{t('status')}</span>
      {!compact && <span style={{ ...cellStyle, textAlign: 'right' }}>{t('latency')}</span>}
    </div>
  )
}

// StreamRow — the dense event row. Click selects (desktop pane) or navigates
// (mobile). The selected row gets a soft-accent wash (never a side-stripe).
function StreamRow({
  event, endpointName, fresh, selected, compact, isMobile, onActivate,
}: {
  event: WebhookEvent
  endpointName?: string
  fresh: boolean
  selected: boolean
  compact: boolean
  isMobile: boolean
  onActivate: () => void
}) {
  const [hovered, setHovered] = useState(false)
  const isErr = event.response_status != null && event.response_status >= 400
  const isWarn = !isErr && event.response_status != null && event.response_status >= 300
  const status = event.response_status ?? '—'
  const statusColor = isErr ? 'var(--red)' : isWarn ? 'var(--amber)' : 'var(--green)'
  const dotColor = event.status === 'failed' ? 'var(--red)' : event.status === 'forwarded' ? 'var(--green)' : 'var(--muted)'

  const time = new Date(event.received_at).toLocaleTimeString('en-GB', {
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  })
  const kind = event.event_type || event.method || 'event'
  const idShort = event.id.slice(0, 8)

  const bg = selected ? 'var(--accent-soft)' : hovered ? 'var(--surface)' : 'transparent'

  return (
    <div
      role="button"
      tabIndex={0}
      aria-current={selected ? 'location' : undefined}
      onClick={onActivate}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onActivate() } }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'grid',
        gridTemplateColumns: compact ? '24px 18px 1fr 64px' : '24px 18px 130px 1fr 110px 72px 68px',
        padding: '0 16px',
        minHeight: isMobile ? 44 : 36,
        alignItems: 'center',
        borderBottom: '1px solid var(--border)',
        backgroundColor: bg,
        cursor: 'pointer',
        fontSize: 12.5,
        color: 'var(--text)',
        animation: fresh ? 'whl-slidein 500ms cubic-bezier(0.22, 1, 0.36, 1)' : undefined,
        transition: 'background 120ms ease',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 6, height: 6, borderRadius: '50%',
          backgroundColor: dotColor,
          animation: fresh ? 'ping-soft 1s ease-in-out 2' : undefined,
        }}
      />
      {/* Wrapper keeps the grid cell even when ProviderMark renders null for a
          generic provider — otherwise the null child collapses the column and
          shifts every following cell left. */}
      <span style={{ display: 'inline-flex', alignItems: 'center', minWidth: 0 }}>
        <ProviderMark name={event.provider} size={14} />
      </span>
      {!compact && (
        <span style={{ fontFamily: 'var(--font-mono, ui-monospace, monospace)', fontSize: 11.5, color: 'var(--muted)' }}>{time}</span>
      )}
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <span style={{
          fontFamily: 'var(--font-mono, ui-monospace, monospace)',
          fontSize: 12, color: 'var(--white)', whiteSpace: 'nowrap',
          overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{kind}</span>
        <span style={{
          fontFamily: 'var(--font-mono, ui-monospace, monospace)',
          fontSize: 10.5, color: 'var(--muted)', whiteSpace: 'nowrap',
        }}>{idShort}</span>
        {event.body && (
          <span style={{
            flex: 1, minWidth: 0,
            fontFamily: 'var(--font-mono, ui-monospace, monospace)',
            fontSize: 11, color: 'var(--muted)', opacity: 0.75,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>{event.body.replace(/\s+/g, ' ').trim()}</span>
        )}
      </span>
      {!compact && (
        <span style={{
          fontFamily: 'var(--font-mono, ui-monospace, monospace)',
          fontSize: 11.5, color: 'var(--muted)',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>{endpointName || '—'}</span>
      )}
      <span style={{
        fontFamily: 'var(--font-mono, ui-monospace, monospace)',
        fontSize: 11.5, color: statusColor, fontWeight: 500,
        textAlign: compact ? 'right' : 'left',
      }}>{status}</span>
      {!compact && (
        <span style={{
          fontFamily: 'var(--font-mono, ui-monospace, monospace)',
          fontSize: 11.5, textAlign: 'right',
          color: event.response_time_ms != null && event.response_time_ms > 250 ? 'var(--amber)' : 'var(--muted)',
        }}>{event.response_time_ms != null ? `${event.response_time_ms}ms` : '—'}</span>
      )}
    </div>
  )
}

// DetailPane — the Discover inspector alongside the list (desktop only). Header
// carries the id + open-full + close; the body is the shared <EventInspector>.
function DetailPane({ eventId, onClose, width }: { eventId: string; onClose: () => void; width: number }) {
  const { data: event, isLoading } = useQuery<WebhookEvent>({
    queryKey: ['event', eventId],
    queryFn: () => api.get<WebhookEvent>(`/api/events/${eventId}`),
    enabled: !!eventId,
  })
  const idShort = eventId.slice(0, 12)

  return (
    <aside style={{
      width: width, flexShrink: 0,
      borderLeft: '1px solid var(--border)',
      backgroundColor: 'var(--bg)',
      display: 'flex', flexDirection: 'column', minHeight: 0,
    }}>
      <div style={{
        height: 48, minHeight: 48,
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '0 8px 0 16px',
        borderBottom: '1px solid var(--border)',
        backgroundColor: 'var(--surface)',
      }}>
        <span style={{
          fontFamily: 'var(--font-mono, ui-monospace, monospace)',
          fontSize: 12.5, color: 'var(--white)', fontWeight: 500,
          minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{idShort}</span>
        <span style={{ flex: 1 }} />
        <Link
          to={`/events/${eventId}`}
          title={t('openFull')}
          aria-label={t('openFull')}
          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)', padding: 4 }}
        >
          <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M14 5h5v5M19 5l-7 7M19 14v4a1 1 0 01-1 1H6a1 1 0 01-1-1V6a1 1 0 011-1h4" />
          </svg>
        </Link>
        <button
          type="button"
          onClick={onClose}
          title={t('close')}
          aria-label={t('close')}
          style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--muted)', padding: 4, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {isLoading ? (
        <div style={{ padding: '2rem', color: 'var(--muted)', fontSize: 13 }}>{t('loading')}</div>
      ) : !event ? (
        <div style={{ padding: '2rem', color: 'var(--muted)', fontSize: 13 }}>{t('eventNotFound')}</div>
      ) : (
        <EventInspector event={event} variant="pane" />
      )}
    </aside>
  )
}

// FootStatus — bottom summary bar: streaming state, counts, page nav.
function FootStatus({
  streaming, loaded, errors, avgMs, page, totalPages, onPrev, onNext,
}: {
  streaming: boolean
  loaded: number
  errors: number
  avgMs: number | null
  page: number
  totalPages: number
  onPrev: () => void
  onNext: () => void
}) {
  return (
    <div style={{
      minHeight: 28,
      borderTop: '1px solid var(--border)',
      padding: '4px 16px',
      display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
      fontSize: 10.5,
      fontFamily: 'var(--font-mono, ui-monospace, monospace)',
      color: 'var(--muted)',
      backgroundColor: 'var(--surface)',
      flexShrink: 0,
    }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
        <span aria-hidden="true" style={{
          width: 6, height: 6, borderRadius: '50%',
          backgroundColor: streaming ? 'var(--green)' : 'var(--muted)',
          animation: streaming ? 'ping-soft 2s ease-in-out infinite' : undefined,
        }} />
        {streaming ? t('streaming') : t('paused')}
      </span>
      <span>{loaded} {t('loaded')}</span>
      <span>{errors} {t('errors')}</span>
      {avgMs != null && <span>{t('avgLatency', { ms: avgMs })}</span>}
      <span style={{ flex: 1 }} />
      {totalPages > 1 && (
        <>
          <span>{t('page')} {page} / {totalPages}</span>
          <button
            type="button"
            onClick={onPrev}
            disabled={page === 1}
            style={{
              background: 'transparent', border: '1px solid var(--border)',
              color: page === 1 ? 'var(--muted)' : 'var(--text)',
              fontFamily: 'var(--font-mono, ui-monospace, monospace)',
              fontSize: 11, padding: '3px 10px', borderRadius: 4,
              cursor: page === 1 ? 'not-allowed' : 'pointer',
              opacity: page === 1 ? 0.4 : 1,
            }}
          >‹</button>
          <button
            type="button"
            onClick={onNext}
            disabled={page >= totalPages}
            style={{
              background: 'transparent', border: '1px solid var(--border)',
              color: page >= totalPages ? 'var(--muted)' : 'var(--text)',
              fontFamily: 'var(--font-mono, ui-monospace, monospace)',
              fontSize: 11, padding: '3px 10px', borderRadius: 4,
              cursor: page >= totalPages ? 'not-allowed' : 'pointer',
              opacity: page >= totalPages ? 0.4 : 1,
            }}
          >›</button>
        </>
      )}
    </div>
  )
}

// HeartbeatStrip — scrubbable activity timeline at the top of the list. It
// follows the selected range so its count, success rate and bars stay in sync
// with the filtered list (intraday periods show times on the axis, multi-day
// periods show dates).
function HeartbeatStrip({
  data, period, active, onHover,
}: {
  data: TimelinePoint[]
  period: string
  active: number | null
  onHover: (i: number | null) => void
}) {
  const totals = data.reduce(
    (acc, d) => { acc.total += d.total; acc.success += d.success; acc.failed += d.failed; return acc },
    { total: 0, success: 0, failed: 0 },
  )
  const successRate = totals.total > 0 ? ((totals.success / totals.total) * 100).toFixed(1) : '—'
  const intraday = period === '1h' || period === '24h'
  const axisLabel = (ts: string) => (intraday ? formatTime(ts) : formatShortDate(ts))
  const focused = active != null ? data[active] : null
  const focusedTime = focused ? axisLabel(focused.timestamp) : null
  const mid = data.length > 0 ? data[Math.floor(data.length / 2)] : null

  return (
    <div style={{ padding: '0.875rem 1rem 0.625rem', borderBottom: '1px solid var(--border)', backgroundColor: 'var(--surface)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '0.375rem', gap: '0.75rem', flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: '0.6875rem', color: 'var(--muted)', letterSpacing: '0.06em', textTransform: 'uppercase', fontWeight: 500 }}>
            {t(HEARTBEAT_LABEL[period] ?? 'heartbeatLast24h')}
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.625rem', marginTop: '0.125rem' }}>
            <span className="font-mono" style={{ fontSize: '1.375rem', fontWeight: 500, letterSpacing: '-0.02em', color: 'var(--white)' }}>
              {(focused ? focused.total : totals.total).toLocaleString()}
            </span>
            <span style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>
              {focused ? t('eventsAtTime', { time: focusedTime ?? '' }) : t('eventsSuccessSuffix', { value: successRate })}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.875rem', fontSize: '0.6875rem', color: 'var(--muted)', fontFamily: 'var(--font-ui)' }}>
          <LegendDot color="var(--accent)" label={t('legendSuccess')} />
          <LegendDot color="var(--red)" label={t('legendErrorHeavy')} />
        </div>
      </div>
      <div onMouseLeave={() => onHover(null)}>
        <HeartbeatBars data={data} active={active} onHover={onHover} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.625rem', color: 'var(--muted)', fontFamily: 'var(--font-mono)', marginTop: '0.25rem' }}>
        <span>{data.length > 0 ? axisLabel(data[0].timestamp) : ''}</span>
        {mid && <span>{axisLabel(mid.timestamp)}</span>}
        <span>{t('now')}</span>
      </div>
    </div>
  )
}

function HeartbeatBars({
  data, active, onHover,
}: {
  data: TimelinePoint[]
  active: number | null
  onHover: (i: number | null) => void
}) {
  const max = Math.max(...data.map(d => d.total), 1)
  const HEIGHT = 42
  return (
    <div style={{ height: HEIGHT, display: 'grid', gridTemplateColumns: `repeat(${data.length}, 1fr)`, gap: 1, alignItems: 'end', padding: '4px 0' }}>
      {data.map((d, i) => {
        const h = Math.max(2, (d.total / max) * (HEIGHT - 8))
        const isErr = d.failed > d.total * 0.3
        const background = isErr ? 'var(--red)' : active === i ? 'var(--white)' : 'var(--accent)'
        return (
          <div
            key={i}
            onMouseEnter={() => onHover(i)}
            onClick={() => onHover(active === i ? null : i)}
            title={`${d.total} events at ${new Date(d.timestamp).toLocaleString()}`}
            style={{
              height: h, background,
              opacity: active != null && active !== i ? 0.35 : 1,
              borderRadius: 1, transition: 'opacity 120ms, background 120ms', cursor: 'pointer',
            }}
          />
        )
      })}
    </div>
  )
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3125rem' }}>
      <span style={{ width: 8, height: 8, borderRadius: 2, background: color }} />
      {label}
    </span>
  )
}

function EmptyState({
  icon, title, description, action,
}: {
  icon: React.ReactNode; title: string; description: string; action?: React.ReactNode
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '16rem', textAlign: 'center', padding: '1.5rem' }}>
      <div style={{ width: '3.5rem', height: '3.5rem', borderRadius: '9999px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--surface-2)', marginBottom: '1rem' }}>{icon}</div>
      <h3 style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--white)', marginBottom: '0.25rem' }}>{title}</h3>
      <p style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>{description}</p>
      {action && <div style={{ marginTop: '0.75rem' }}>{action}</div>}
    </div>
  )
}

// Provider-specific "point something at your endpoint" snippets. curl is first
// (universal); Stripe/GitHub cover the two most common real sources. Mirrors the
// vitrine's try-tabs so the product reads as one surface.
const FIRST_RUN_TABS = ['curl', 'stripe', 'gh'] as const
type FirstRunTab = typeof FIRST_RUN_TABS[number]
const FIRST_RUN_TAB_LABEL: Record<FirstRunTab, string> = { curl: 'curl', stripe: 'Stripe CLI', gh: 'GitHub' }

function firstRunSnippet(tab: FirstRunTab, url: string): string {
  switch (tab) {
    case 'stripe':
      return `stripe listen --forward-to ${url}`
    case 'gh':
      return `# Repo → Settings → Webhooks → Add webhook\nPayload URL:  ${url}\nContent type: application/json`
    default:
      return `curl -X POST ${url} \\\n  -H "Content-Type: application/json" \\\n  -d '{"event":"test"}'`
  }
}

function FirstRunEmptyState({ exampleUrl }: { exampleUrl: string }) {
  const urlCopy = useCopy()
  const snipCopy = useCopy()
  const [tab, setTab] = useState<FirstRunTab>('curl')
  const snippet = firstRunSnippet(tab, exampleUrl)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '3rem 1.5rem', textAlign: 'center' }}>
      {/* Live indicator: the page is already listening, so say so. */}
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.125rem' }}>
        <span style={{ width: 8, height: 8, borderRadius: '9999px', backgroundColor: 'var(--green)', animation: 'ping-soft 2s ease-in-out infinite' }} />
        <span style={{ fontSize: '0.75rem', fontWeight: 500, color: 'var(--green)', letterSpacing: '0.02em' }}>{t('listeningForEvents')}</span>
      </div>

      <h3 style={{ fontSize: '1.0625rem', fontWeight: 600, color: 'var(--text)', marginBottom: '0.375rem' }}>{t('noWebhooksTitle')}</h3>
      <p style={{ fontSize: '0.8125rem', color: 'var(--muted)', marginBottom: '1.5rem', maxWidth: '34rem', lineHeight: 1.5 }}>
        {t('noWebhooksDesc')}
      </p>

      {/* The endpoint URL — the thing they paste into a provider. */}
      <div style={{ width: '100%', maxWidth: '34rem', textAlign: 'left', marginBottom: '1.125rem' }}>
        <p style={{ fontSize: '0.6875rem', fontWeight: 600, color: 'var(--muted)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '0.375rem' }}>{t('yourEndpointUrl')}</p>
        <div style={{ display: 'flex', alignItems: 'stretch', border: '1px solid var(--accent)', borderRadius: '0.5rem', overflow: 'hidden' }}>
          <code style={{ flex: 1, minWidth: 0, padding: '0.625rem 0.75rem', fontSize: '0.8125rem', fontFamily: 'ui-monospace, monospace', color: 'var(--accent-ink)', backgroundColor: 'var(--surface)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{exampleUrl}</code>
          <button type="button" onClick={() => urlCopy.copy(exampleUrl)} style={{ flexShrink: 0, fontSize: '0.75rem', fontWeight: 500, padding: '0 0.875rem', border: 'none', borderLeft: '1px solid var(--accent)', cursor: 'pointer', backgroundColor: urlCopy.copied ? 'color-mix(in srgb, var(--green) 15%, transparent)' : 'var(--surface-2)', color: urlCopy.copied ? 'var(--green)' : 'var(--text)' }}>
            {urlCopy.copied ? t('copied') : t('copy')}
          </button>
        </div>
      </div>

      {/* Provider tabs: how to point a real source at the URL above. */}
      <div style={{ width: '100%', maxWidth: '34rem', textAlign: 'left' }}>
        <div style={{ display: 'flex', gap: '0.375rem', marginBottom: '0.5rem' }}>
          {FIRST_RUN_TABS.map(k => (
            <button key={k} type="button" onClick={() => setTab(k)} aria-pressed={tab === k}
              style={{ fontSize: '0.75rem', fontWeight: 500, padding: '0.3rem 0.7rem', borderRadius: '0.375rem', cursor: 'pointer',
                border: '1px solid ' + (tab === k ? 'var(--accent)' : 'var(--border)'),
                backgroundColor: tab === k ? 'var(--accent-soft)' : 'transparent',
                color: tab === k ? 'var(--accent-ink)' : 'var(--muted)' }}>
              {FIRST_RUN_TAB_LABEL[k]}
            </button>
          ))}
        </div>
        <div style={{ borderRadius: '0.5rem', border: '1px solid var(--border)', backgroundColor: 'var(--surface)', padding: '0.75rem 1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.25rem' }}>
            <button type="button" onClick={() => snipCopy.copy(snippet)} style={{ fontSize: '0.6875rem', fontWeight: 500, padding: '0.2rem 0.55rem', borderRadius: '0.25rem', border: 'none', cursor: 'pointer', backgroundColor: snipCopy.copied ? 'color-mix(in srgb, var(--green) 15%, transparent)' : 'var(--surface-2)', color: snipCopy.copied ? 'var(--green)' : 'var(--text)' }}>
              {snipCopy.copied ? t('copied') : t('copy')}
            </button>
          </div>
          <code style={{ display: 'block', fontSize: '0.75rem', fontFamily: 'ui-monospace, monospace', color: 'var(--accent-ink)', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
            {snippet}
          </code>
        </div>
      </div>

      <p style={{ marginTop: '1.25rem', fontSize: '0.75rem', color: 'var(--muted)', opacity: 0.85 }}>
        {t('firstEventRealtime')}
      </p>
    </div>
  )
}
